import React, { ChangeEventHandler, FunctionComponent, useCallback, useMemo } from "react";
import { Box, Grid } from "@mui/material";
import { noop } from "lodash";
import { saveAs } from "file-saver";
import { Button, Chip, RoundedIcon } from "@shared/components";
import { investorRequestSelector, hasUnsavedChangesSelector } from "@investorRequest/store/selectors";
import {
  updateCommonRequestData,
  clearDraftAttachmentId,
  loadDraftData,
  saveAttachmentToSession,
  clearSessionAttachment,
} from "@investorRequest/store/ducks";
import { selectInternalExternalPresence } from "@store/Entities/ducks";
import FileUtils from "@shared/utils/FileUtils";
import useAttachmentValidator from "@investorRequest/hooks/useAttacmentValidator";
import { MimeType } from "@shared/models/mimeType";
import { useAppDispatch, useAppSelector, useMobileLayout } from "@app/hooks";
import { ReactComponent as Clip } from "@assets/svg/clip.svg";
import { ReactComponent as ExcelIcon } from "@assets/svg/file_excel.svg";
import { ReactComponent as ImageIcon } from "@assets/svg/file_image.svg";
import { ReactComponent as PdfIcon } from "@assets/svg/file_pdf.svg";
import { ReactComponent as PowerpointIcon } from "@assets/svg/file_powerpoint.svg";
import { ReactComponent as WordIcon } from "@assets/svg/file_word.svg";
import { ReactComponent as DocumentIcon } from "@assets/svg/file_document.svg";
import { ReactComponent as ExclamationIcon } from "@assets/svg/exclamation.svg";
import { ReactComponent as NotRegisteredEventIcon } from "@assets/svg/not_registered_event_icon.svg";
import { saveDraftThunk, deleteDraftThunk, downloadAttachmentThunk } from "@investorRequest/store/thunks";
import { customFieldsMap } from "@investorRequest/constants";
import { InvestorRequestType } from "@investorRequest/models";

interface Props {
  loading: boolean;
  onSubmit(): void;
}

// LPC-9775: Wire Instructions specific controls with Save Draft, Attach, and Clear buttons
const WireInstructionsControls: FunctionComponent<Props> = ({ onSubmit, loading }) => {
  const { requestType, validationWarningShown, commonFields, customFields, activeInfoTab, draft } =
    useAppSelector(investorRequestSelector);
  const { attachment, selectedFunds, selectedAccounts, message } = commonFields;
  const { hasInternal, hasExternal } = useAppSelector((state) => selectInternalExternalPresence(state as any));
  const hasUnsavedChanges = useAppSelector(hasUnsavedChangesSelector);

  const dispatch = useAppDispatch();

  const { validateAttachment, allowedExtensions } = useAttachmentValidator();

  const accept = useMemo(
    () => `image/png, ${allowedExtensions.map((ext) => `.${ext}`).join(", ")}`,
    [allowedExtensions]
  );

  const AttachmentIcon = useMemo(() => {
    switch (attachment?.type) {
      case MimeType.JPEG:
      case MimeType.PNG:
        return ImageIcon;
      case MimeType.PDF:
        return PdfIcon;
      case MimeType.DOCX:
        return WordIcon;
      case MimeType.PPTX:
        return PowerpointIcon;
      case MimeType.XLSX:
        return ExcelIcon;
      default:
        return DocumentIcon;
    }
  }, [attachment]);

  // LPC-9775: Determine fund type (Internal or External) based on active tab or user entitlements
  const getFundType = useCallback(() => {
    // First, check if fundType is already set in customFields (most reliable)
    if (customFields?.fundType) {
      return customFields.fundType;
    }

    // For hybrid users (has both Internal and External), use activeInfoTab
    if (hasInternal && hasExternal) {
      return activeInfoTab === "Internal Fund Information" ? "Internal" : "External";
    }

    // For non-hybrid users, determine based on what they have
    if (hasInternal && !hasExternal) {
      return "Internal";
    }

    if (!hasInternal && hasExternal) {
      return "External";
    }

    // Default fallback (should rarely happen)
    return "Internal";
  }, [hasInternal, hasExternal, activeInfoTab, customFields]);

  const handleAttachFile = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (event) => {
      if (!event.target.files) return;

      const file = event.target.files[0];

      if (!validateAttachment(file)) return;

      const base64 = await FileUtils.toBase64String(file);

      dispatch(
        updateCommonRequestData({
          attachment: {
            base64,
            name: file.name,
            type: file.type as MimeType,
          },
        })
      );

      // Clear saved attachment ID since user attached a new file
      dispatch(clearDraftAttachmentId());

      // LPC-9775: Save to session storage (for hybrid users switching tabs)
      const fundType = getFundType();

      dispatch(
        saveAttachmentToSession({
          fundType,
          attachment: {
            attachmentName: file.name,
            attachmentFile: {
              name: file.name,
              type: file.type,
              base64,
            },
          },
        })
      );
    },
    [dispatch, validateAttachment, getFundType]
  );

  const handleRemoveAttachment = useCallback(() => {
    dispatch(updateCommonRequestData({ attachment: null }));
    dispatch(clearDraftAttachmentId());

    // LPC-9775: Clear from session storage as well
    const fundType = getFundType();

    dispatch(clearSessionAttachment(fundType));
  }, [dispatch, getFundType]);

  // LPC-9775: Save as Draft handler
  const handleSaveAsDraft = useCallback(async () => {
    const fundType = getFundType();

    // Convert base64 string to byte array
    const byteArray = attachment?.base64
      ? Array.from(atob(attachment.base64), (char) => char.charCodeAt(0))
      : undefined;

    const payload = {
      fundType,
      jsonFormData: JSON.stringify({
        commonFields: {
          selectedFunds,
          selectedAccounts,
          message,
        },
        customFields,
      }),
      attachmentName: attachment?.name,
      attachment: byteArray,
    };

    await dispatch(saveDraftThunk(payload));
  }, [dispatch, getFundType, attachment, selectedFunds, selectedAccounts, message, customFields]);

  // LPC-9775: Clear All Fields handler
  const handleClearAllFields = useCallback(async () => {
    const fundType = getFundType();

    // Call delete draft API
    await dispatch(deleteDraftThunk({ fundType }));

    // Clear attachment from commonFields
    dispatch(updateCommonRequestData({ attachment: null }));

    // LPC-9775: Clear session attachment for current fundType
    dispatch(clearSessionAttachment(fundType));

    // Reset form to initial state for wire instructions
    dispatch(
      loadDraftData({
        commonFields: {
          selectedFunds: [],
          selectedAccounts: [],
          message: "",
          attachment: null,
        },
        customFields: customFieldsMap[InvestorRequestType.UpdateWireInstructions],
        isActualDraft: false, // Don't set baseline after clearing
      })
    );
  }, [dispatch, getFundType]);

  // LPC-9775: Download attachment from S3
  const handleDownloadAttachment = useCallback(async () => {
    if (!draft.attachmentId) {
      return;
    }

    const fileName = draft.attachmentName || attachment?.name || "attachment";

    try {
      const result = await dispatch(downloadAttachmentThunk(draft.attachmentId));

      if (downloadAttachmentThunk.fulfilled.match(result)) {
        const blob = result.payload;

        saveAs(blob, fileName);
      }
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  }, [dispatch, draft.attachmentId, draft.attachmentName, attachment?.name]);

  const isMobile = useMobileLayout();

  // Only show for Update Wire Instructions when payment method is selected
  if (requestType !== InvestorRequestType.UpdateWireInstructions || !customFields.paymentMethod) {
    return null;
  }

  return (
    <>
      {attachment && (
        <Box pl={5} pb={isMobile ? 4 : undefined}>
          {draft.attachmentId ? (
            // Draft attachment - clickable for download
            <Box
              onClick={handleDownloadAttachment}
              sx={{
                cursor: "pointer",
                display: "inline-block",
                "&:hover": { opacity: 0.8 },
              }}
              title="Click to download attachment"
            >
              {/* LPC-9775: Wrap chip in a div that stops propagation on delete click */}
              <Box
                onClick={(e: React.MouseEvent) => {
                  // Stop propagation if clicking on the delete icon (X button)
                  const target = e.target as HTMLElement;

                  if (target.closest('[data-testid*="chip-delete"]')) {
                    e.stopPropagation();
                  }
                }}
              >
                <Chip Icon={AttachmentIcon} label={attachment.name} onDelete={handleRemoveAttachment} />
              </Box>
            </Box>
          ) : (
            // Session attachment - not clickable (not saved yet)
            <Chip Icon={AttachmentIcon} label={attachment.name} onDelete={handleRemoveAttachment} />
          )}
          <Box fontSize={14} mt={2.5}>
            Up to one document may be attached; max file size of 5MB
            {draft.attachmentId && " • Click attachment to download"}
          </Box>
        </Box>
      )}
      <Grid
        item
        container
        sx={{
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          width: "100%",
          rowGap: 2,
        }}
        data-testid="wire-instructions-controls"
      >
        {/* First row: Save as Draft, Attach a Document, Clear All Fields */}
        <Grid
          item
          container
          sx={{
            flexDirection: { xs: "column", lg: "row" }, // column on mobile (xs-md), row on desktop (lg+)
            flexWrap: "nowrap",
            gap: 2,
            width: "100%",
          }}
        >
          {/* LPC-9775: Save as Draft button */}
          <Grid item sx={{ flexShrink: 0, width: { xs: "100%", lg: "auto" }, flex: { xs: "none", lg: 1 } }}>
            <Button
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={handleSaveAsDraft}
              shape="rounded"
              variant="outlined"
              startIcon={<NotRegisteredEventIcon />}
              disabled={loading || !customFields.paymentMethod || !hasUnsavedChanges}
            >
              Save as Draft
            </Button>
          </Grid>

          {/* Attach a Document button */}
          <Grid item sx={{ flexShrink: 0, width: { xs: "100%", lg: "auto" }, flex: { xs: "none", lg: 1 } }}>
            <Button
              upload
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={noop}
              shape="rounded"
              variant="outlined"
              startIcon={<Clip />}
              disabled={loading || !customFields.paymentMethod || !!attachment}
            >
              Attach a document
              <input
                type="file"
                id="attachment"
                name="attachment"
                hidden
                accept={accept}
                value=""
                onChange={handleAttachFile}
              />
            </Button>
          </Grid>

          {/* LPC-9775: Clear All Fields button */}
          <Grid item sx={{ flexShrink: 0, width: { xs: "100%", lg: "auto" }, flex: { xs: "none", lg: 1 } }}>
            <Button
              fullWidth
              size={isMobile ? "small" : "medium"}
              onClick={handleClearAllFields}
              shape="rounded"
              variant="outlined"
              disabled={loading || !customFields.paymentMethod}
            >
              Clear All Fields
            </Button>
          </Grid>
        </Grid>

        {/* Second row: Submit button - always on its own line */}
        <Grid item sx={{ flexShrink: 0, width: { xs: "100%", lg: "auto" } }}>
          <Button
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
            onClick={onSubmit}
            shape="rounded"
            disabled={loading || !customFields.paymentMethod}
          >
            Submit
          </Button>
        </Grid>
      </Grid>

      {/* Validation error - displayed after Submit button row */}
      {validationWarningShown && (
        <Grid
          item
          xs={12}
          sx={{
            width: "100%",
            mt: 2,
          }}
        >
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" color="warning.main">
            <Box fontSize={15} fontWeight="500" display="flex" alignItems="center">
              <RoundedIcon Icon={ExclamationIcon} variant="warning" />
              <Box ml={2}>ERROR</Box>
            </Box>
            <Box fontSize={18}>Please finish filling out information</Box>
          </Box>
        </Grid>
      )}
    </>
  );
};

export default WireInstructionsControls;
